import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '../../../environments/environment';

export type StorefrontTrafficEventType = 'page_view' | 'click' | 'heartbeat';

interface QueuedTrafficEvent {
  eventType: StorefrontTrafficEventType;
  pagePath?: string | null;
  clickTarget?: string | null;
  activeSeconds: number;
}

/**
 * Lightweight first-party traffic tracker.
 * Unique visitor = long-lived first-party id (cookie + localStorage), not a server round-trip.
 * Events are queued and flushed in batches so tracking does not block UI / navigation.
 */
@Injectable({
  providedIn: 'root',
})
export class StorefrontTrafficService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly apiUrl = environment.apiUrl;

  private readonly sessionStorageKey = `${environment.channelCode}:traffic-session`;
  private readonly visitorStorageKey = `${environment.channelCode}:traffic-visitor`;
  private readonly visitorCookieName = `pg_vid_${environment.channelCode}`;
  private readonly attributionStorageKey = `${environment.channelCode}:traffic-attribution`;

  private sessionKey: string | null = null;
  private visitorKey: string | null = null;
  private lastPath = '';
  private lastActiveAt = 0;
  private lastClickAt = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private clickListenerAttached = false;
  private attributionCaptured = false;
  private flushing = false;

  private readonly queue: QueuedTrafficEvent[] = [];
  private readonly maxQueueBeforeFlush = 6;
  private readonly flushDelayMs = 2500;
  private readonly clickThrottleMs = 2000;

  /** Call once from the browser root (e.g. App constructor). */
  bootstrap(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.ensureIds();
    this.captureAttributionFromLocation();
    this.attachClickListener();
    this.startHeartbeat();
    this.attachUnloadHandlers();

    const path = this.normalizedPath(window.location.pathname);
    this.lastPath = path;
    this.lastActiveAt = Date.now();
    // First page view flushes soon so the session exists for the report.
    this.enqueue('page_view', { pagePath: path, activeSeconds: 0 }, true);
  }

  /** Call on every client-side NavigationEnd. */
  trackNavigation(nextUrl: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.ensureIds();
    this.captureAttributionFromLocation();

    const nextPath = this.normalizedPath(nextUrl);
    if (nextPath === this.lastPath) {
      return;
    }

    const now = Date.now();
    const elapsedSeconds =
      this.lastActiveAt > 0 ? Math.floor((now - this.lastActiveAt) / 1000) : 0;

    if (this.lastPath && elapsedSeconds > 0) {
      this.enqueue('heartbeat', {
        pagePath: this.lastPath,
        activeSeconds: Math.min(Math.max(elapsedSeconds, 0), 120),
      });
    }

    this.lastPath = nextPath;
    this.lastActiveAt = now;
    this.enqueue('page_view', { pagePath: nextPath, activeSeconds: 0 });
  }

  private ensureIds(): void {
    if (!this.visitorKey) {
      this.visitorKey = this.resolveVisitorKey();
    }
    if (!this.sessionKey) {
      this.sessionKey = this.readOrCreateSessionKey();
    }
  }

  /**
   * Unique visitor id: cookie (1 year) preferred, with localStorage fallback.
   * Created only once per browser — never requires a server call.
   */
  private resolveVisitorKey(): string {
    const fromCookie = this.readCookie(this.visitorCookieName);
    if (fromCookie && fromCookie.length >= 8) {
      this.persistVisitorKey(fromCookie);
      return fromCookie;
    }

    try {
      const fromLocal = localStorage.getItem(this.visitorStorageKey);
      if (fromLocal && fromLocal.length >= 8) {
        this.persistVisitorKey(fromLocal);
        return fromLocal;
      }
    } catch {
      /* private mode */
    }

    const created = this.newId();
    this.persistVisitorKey(created);
    return created;
  }

  private persistVisitorKey(id: string): void {
    try {
      localStorage.setItem(this.visitorStorageKey, id);
    } catch {
      /* ignore */
    }
    this.writeCookie(this.visitorCookieName, id, 365);
  }

  private readOrCreateSessionKey(): string {
    try {
      const existing = sessionStorage.getItem(this.sessionStorageKey);
      if (existing && existing.length >= 8) {
        return existing;
      }
    } catch {
      /* ignore */
    }

    const created = this.newId();
    try {
      sessionStorage.setItem(this.sessionStorageKey, created);
    } catch {
      /* ignore */
    }
    return created;
  }

  private newId(): string {
    return typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `t-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private readCookie(name: string): string | null {
    try {
      const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  }

  private writeCookie(name: string, value: string, days: number): void {
    try {
      const maxAge = days * 24 * 60 * 60;
      document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
    } catch {
      /* ignore */
    }
  }

  private captureAttributionFromLocation(): void {
    if (this.attributionCaptured) {
      return;
    }

    try {
      const cached = sessionStorage.getItem(this.attributionStorageKey);
      if (cached) {
        this.attributionCaptured = true;
        return;
      }
    } catch {
      /* ignore */
    }

    const params = new URLSearchParams(window.location.search);
    const payload = {
      referrerUrl: document.referrer || null,
      utmSource: params.get('utm_source'),
      utmMedium: params.get('utm_medium'),
      utmCampaign: params.get('utm_campaign'),
      sharedLinkType: this.resolveSharedLinkType(params),
    };

    try {
      sessionStorage.setItem(this.attributionStorageKey, JSON.stringify(payload));
    } catch {
      /* ignore */
    }
    this.attributionCaptured = true;
  }

  private resolveSharedLinkType(params: URLSearchParams): string | null {
    if (params.get('ref')) {
      return 'referral';
    }
    if (params.get('aff') || params.get('affiliate')) {
      return 'affiliate';
    }
    if (params.get('src')) {
      return 'campaign';
    }
    return null;
  }

  private readAttribution(): {
    referrerUrl: string | null;
    utmSource: string | null;
    utmMedium: string | null;
    utmCampaign: string | null;
    sharedLinkType: string | null;
  } {
    try {
      const raw = sessionStorage.getItem(this.attributionStorageKey);
      if (raw) {
        return JSON.parse(raw) as {
          referrerUrl: string | null;
          utmSource: string | null;
          utmMedium: string | null;
          utmCampaign: string | null;
          sharedLinkType: string | null;
        };
      }
    } catch {
      /* ignore */
    }

    return {
      referrerUrl: document.referrer || null,
      utmSource: null,
      utmMedium: null,
      utmCampaign: null,
      sharedLinkType: null,
    };
  }

  private attachClickListener(): void {
    if (this.clickListenerAttached) {
      return;
    }
    this.clickListenerAttached = true;

    document.addEventListener(
      'click',
      (event) => {
        const now = Date.now();
        if (now - this.lastClickAt < this.clickThrottleMs) {
          return;
        }

        const target = event.target as HTMLElement | null;
        if (!target) {
          return;
        }

        const interactive = target.closest('a,button,[role="button"],input[type="submit"]') as
          | HTMLElement
          | null;
        if (!interactive) {
          return;
        }

        this.lastClickAt = now;
        const label =
          interactive.getAttribute('data-track') ||
          interactive.getAttribute('aria-label') ||
          (interactive instanceof HTMLAnchorElement ? interactive.getAttribute('href') : null) ||
          interactive.id ||
          interactive.tagName.toLowerCase();

        this.enqueue('click', {
          pagePath: this.lastPath || this.normalizedPath(window.location.pathname),
          clickTarget: (label || 'click').slice(0, 500),
          activeSeconds: 0,
        });
      },
      { capture: true, passive: true },
    );
  }

  private startHeartbeat(): void {
    if (this.heartbeatTimer) {
      return;
    }

    // Accumulate time locally; flush with the queue (no dedicated HTTP per tick).
    this.heartbeatTimer = setInterval(() => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      const now = Date.now();
      const elapsedSeconds =
        this.lastActiveAt > 0 ? Math.floor((now - this.lastActiveAt) / 1000) : 0;
      if (elapsedSeconds < 20) {
        return;
      }

      this.enqueue('heartbeat', {
        pagePath: this.lastPath || this.normalizedPath(window.location.pathname),
        activeSeconds: Math.min(elapsedSeconds, 120),
      });
      this.lastActiveAt = now;
    }, 45000);
  }

  private attachUnloadHandlers(): void {
    const flush = () => {
      const now = Date.now();
      const elapsedSeconds =
        this.lastActiveAt > 0 ? Math.floor((now - this.lastActiveAt) / 1000) : 0;
      if (elapsedSeconds > 0) {
        this.enqueue('heartbeat', {
          pagePath: this.lastPath || this.normalizedPath(window.location.pathname),
          activeSeconds: Math.min(elapsedSeconds, 120),
        });
      }
      this.flushQueue(true);
    };

    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flush();
      } else {
        this.lastActiveAt = Date.now();
      }
    });
  }

  private enqueue(
    eventType: StorefrontTrafficEventType,
    extras: {
      pagePath?: string | null;
      clickTarget?: string | null;
      activeSeconds?: number;
    },
    flushSoon = false,
  ): void {
    // Merge consecutive heartbeats into one queued item to cut request volume.
    if (eventType === 'heartbeat' && this.queue.length > 0) {
      const last = this.queue[this.queue.length - 1];
      if (last.eventType === 'heartbeat') {
        last.activeSeconds = Math.min(
          last.activeSeconds + (extras.activeSeconds ?? 0),
          120,
        );
        last.pagePath = extras.pagePath ?? last.pagePath;
        this.scheduleFlush(flushSoon);
        return;
      }
    }

    this.queue.push({
      eventType,
      pagePath: extras.pagePath,
      clickTarget: extras.clickTarget,
      activeSeconds: extras.activeSeconds ?? 0,
    });

    if (this.queue.length >= this.maxQueueBeforeFlush) {
      this.flushQueue(false);
      return;
    }

    this.scheduleFlush(flushSoon);
  }

  private scheduleFlush(soon: boolean): void {
    if (this.flushTimer) {
      if (!soon) {
        return;
      }
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    this.flushTimer = setTimeout(
      () => {
        this.flushTimer = null;
        this.flushQueue(false);
      },
      soon ? 200 : this.flushDelayMs,
    );
  }

  private flushQueue(useKeepalive: boolean): void {
    if (this.flushing || this.queue.length === 0) {
      return;
    }

    this.flushing = true;
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    const batch = this.queue.splice(0, this.queue.length);
    this.ensureIds();
    const attribution = this.readAttribution();

    const sendOne = (item: QueuedTrafficEvent): Promise<void> => {
      const body = {
        sessionKey: this.sessionKey,
        visitorKey: this.visitorKey,
        eventType: item.eventType,
        pagePath: item.pagePath,
        clickTarget: item.clickTarget,
        referrerUrl: attribution.referrerUrl,
        utmSource: attribution.utmSource,
        utmMedium: attribution.utmMedium,
        utmCampaign: attribution.utmCampaign,
        sharedLinkType: attribution.sharedLinkType,
        activeSeconds: item.activeSeconds,
      };

      return fetch(`${this.apiUrl}Storefront/TrackTraffic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Channel-Code': environment.channelCode,
        },
        body: JSON.stringify(body),
        keepalive: useKeepalive,
        credentials: 'omit',
      }).then(
        () => undefined,
        () => undefined,
      );
    };

    // Sequential fire-and-forget keeps order without blocking the UI thread.
    void (async () => {
      try {
        for (const item of batch) {
          await sendOne(item);
        }
      } finally {
        this.flushing = false;
        if (this.queue.length > 0) {
          this.scheduleFlush(false);
        }
      }
    })();
  }

  private normalizedPath(url: string): string {
    const path = url.split('?')[0].split('#')[0];
    return path || '/';
  }
}
